import { Element } from '../Element';

import type {
  TranscriptColor,
  TranscriptEffect,
  TranscriptMaximumLength,
  TranscriptPlacement,
  TranscriptSource,
  TranscriptSplit,
} from '../../types/karaoke';

/**
 * Interface for legacy text element properties that may exist in JSON data
 */
interface LegacyTextProps {
  fontFamily?: string;
  fontSize?: number | string | null;
  color?: string;
}

/**
 * Interface for text element state
 */
interface TextElementState {
  text: string;
  font_family: string;
  font_weight: number | string;
  font_style: string;
  font_size: number | string | null;
  font_size_minimum: string;
  font_size_maximum: string;
  letter_spacing: string;
  line_height: string;
  text_wrap: boolean;
  text_clip: boolean;
  text_transform: string;
  background_color: string | null;
  background_x_padding: string;
  background_y_padding: string;
  background_border_radius: string;
  background_align_threshold: string;
  transcript_source?: TranscriptSource;
  transcript_effect?: TranscriptEffect;
  transcript_split?: TranscriptSplit;
  transcript_placement?: TranscriptPlacement;
  transcript_maximum_length?: TranscriptMaximumLength;
  transcript_color?: TranscriptColor;
}

/**
 * TextElement class representing a text element in the timeline
 */
export class TextElement extends Element {
  // Core text properties
  text: string = '';
  font_family: string = 'Aileron';
  font_weight: number | string = 400;
  font_style: string = 'normal';
  font_size: number | string | null = null;
  font_size_minimum: string = '1 vmin';
  font_size_maximum: string = '100 vmin';
  // Note: fill_color, x_alignment, y_alignment are now inherited from Element base class

  // Text alignment and spacing
  letter_spacing: string = '0%';
  line_height: string = '115%';

  // Text behavior
  text_wrap: boolean = true;
  text_clip: boolean = false;
  text_transform: string = 'none'; // none, capitalize, uppercase, lowercase

  // Background properties
  background_color: string | null = null;
  background_x_padding: string = '25%';
  background_y_padding: string = '25%';
  background_border_radius: string = '0%';
  background_align_threshold: string = '20%';

  // Transcript properties
  transcript_source?: TranscriptSource;
  transcript_effect?: TranscriptEffect;
  transcript_split?: TranscriptSplit;
  transcript_placement?: TranscriptPlacement;
  transcript_maximum_length?: TranscriptMaximumLength;
  transcript_color?: TranscriptColor;

  constructor(props: Partial<TextElement & LegacyTextProps> = {}) {
    super({ ...props, type: 'text' });

    // Core text properties
    this.text = props.text ?? '';
    this.font_family = props.font_family ?? props.fontFamily ?? 'Aileron';
    this.font_weight = props.font_weight ?? 400;
    this.font_style = props.font_style ?? 'normal';
    this.font_size = props.font_size ?? props.fontSize ?? null;
    this.font_size_minimum = props.font_size_minimum ?? '1 vmin';
    this.font_size_maximum = props.font_size_maximum ?? '100 vmin';

    this._height = props.height ?? null; // TextElement default
    this._width = props.width ?? null; // TextElement default

    // Set TextElement-specific defaults for inherited properties
    // Override Element defaults with TextElement-specific values
    super.update({
      fill_color: props.fill_color ?? props.color ?? '#000000',
      x_alignment: props.x_alignment ?? '0%',
      y_alignment: props.y_alignment ?? '0%',
    });

    // Text alignment and spacing
    this.letter_spacing = props.letter_spacing ?? '0%';
    this.line_height = props.line_height ?? '115%';

    // Text behavior
    this.text_wrap = props.text_wrap ?? true;
    this.text_clip = props.text_clip ?? false;
    this.text_transform = props.text_transform ?? 'none';

    // Background properties
    this.background_color = props.background_color ?? null;
    this.background_x_padding = props.background_x_padding ?? '25%';
    this.background_y_padding = props.background_y_padding ?? '25%';
    this.background_border_radius = props.background_border_radius ?? '0%';
    this.background_align_threshold = props.background_align_threshold ?? '20%';

    // Transcript properties
    this.transcript_source = props.transcript_source;
    this.transcript_effect = props.transcript_effect;
    this.transcript_split = props.transcript_split;
    this.transcript_placement = props.transcript_placement;
    this.transcript_maximum_length = props.transcript_maximum_length;
    this.transcript_color = props.transcript_color;
  }

  /**
   * Captures the mutable state of the text element.
   * @returns A record of all mutable properties.
   */
  override getState(): Record<string, unknown> & TextElementState {
    const state = {
      ...super.getState(),
      // Core text properties
      text: this.text,
      font_family: this.font_family,
      font_weight: this.font_weight,
      font_style: this.font_style,
      font_size: this.font_size,
      font_size_minimum: this.font_size_minimum,
      font_size_maximum: this.font_size_maximum,
      // Note: fill_color, x_alignment, y_alignment are now included in super.getState()
      // Text alignment and spacing
      letter_spacing: this.letter_spacing,
      line_height: this.line_height,
      // Text behavior
      text_wrap: this.text_wrap,
      text_clip: this.text_clip,
      text_transform: this.text_transform,
      // Background properties
      background_color: this.background_color,
      background_x_padding: this.background_x_padding,
      background_y_padding: this.background_y_padding,
      background_border_radius: this.background_border_radius,
      background_align_threshold: this.background_align_threshold,
      // Transcript properties
      transcript_source: this.transcript_source,
      transcript_effect: this.transcript_effect,
      transcript_split: this.transcript_split,
      transcript_placement: this.transcript_placement,
      transcript_maximum_length: this.transcript_maximum_length,
      transcript_color: this.transcript_color,
    };

    return state;
  }

  /**
   * Restores the text element's mutable state from a state record.
   * @param state The state to restore.
   */
  override restoreState(
    state: Record<string, unknown> & Partial<TextElementState>,
  ): void {
    super.restoreState(state);

    // Core text properties
    if (state.text !== undefined) this.text = state.text;
    if (state.font_family !== undefined) this.font_family = state.font_family;
    if (state.font_weight !== undefined) this.font_weight = state.font_weight;
    if (state.font_style !== undefined) this.font_style = state.font_style;
    if (state.font_size !== undefined) this.font_size = state.font_size;
    if (state.font_size_minimum !== undefined)
      this.font_size_minimum = state.font_size_minimum;
    if (state.font_size_maximum !== undefined)
      this.font_size_maximum = state.font_size_maximum;
    // Note: fill_color, x_alignment, y_alignment are now handled by Element.restoreState()

    // Text alignment and spacing
    if (state.letter_spacing !== undefined)
      this.letter_spacing = state.letter_spacing;
    if (state.line_height !== undefined) this.line_height = state.line_height;

    // Text behavior
    if (state.text_wrap !== undefined) this.text_wrap = state.text_wrap;
    if (state.text_clip !== undefined) this.text_clip = state.text_clip;
    if (state.text_transform !== undefined)
      this.text_transform = state.text_transform;

    // Background properties
    if (state.background_color !== undefined)
      this.background_color = state.background_color;
    if (state.background_x_padding !== undefined)
      this.background_x_padding = state.background_x_padding;
    if (state.background_y_padding !== undefined)
      this.background_y_padding = state.background_y_padding;
    if (state.background_border_radius !== undefined)
      this.background_border_radius = state.background_border_radius;
    if (state.background_align_threshold !== undefined)
      this.background_align_threshold = state.background_align_threshold;

    // Transcript properties
    if (state.transcript_source !== undefined)
      this.transcript_source = state.transcript_source;
    if (state.transcript_effect !== undefined)
      this.transcript_effect = state.transcript_effect;
    if (state.transcript_split !== undefined)
      this.transcript_split = state.transcript_split;
    if (state.transcript_placement !== undefined)
      this.transcript_placement = state.transcript_placement;
    if (state.transcript_maximum_length !== undefined)
      this.transcript_maximum_length = state.transcript_maximum_length;
    if (state.transcript_color !== undefined)
      this.transcript_color = state.transcript_color;
  }

  override defaultFor(prop: string): string | null | undefined {
    // Text boxes auto-size (width/height null) and left-align (x_alignment '0%').
    if (prop === 'width' || prop === 'height') return null;
    if (prop === 'x_alignment') return '0%';
    return super.defaultFor(prop);
  }

  /**
   * Updates the text element's properties
   * @param props Properties to update
   */
  override update(props: Partial<TextElement>): void {
    super.update(props);

    // Core text properties
    if (props.text !== undefined) this.text = props.text;
    if (props.font_family !== undefined) this.font_family = props.font_family;
    if (props.font_weight !== undefined) this.font_weight = props.font_weight;
    if (props.font_style !== undefined) this.font_style = props.font_style;
    if (props.font_size !== undefined) this.font_size = props.font_size;
    if (props.font_size_minimum !== undefined)
      this.font_size_minimum = props.font_size_minimum;
    if (props.font_size_maximum !== undefined)
      this.font_size_maximum = props.font_size_maximum;
    // Note: fill_color, x_alignment, y_alignment are now handled by Element.update()

    // Text alignment and spacing
    if (props.letter_spacing !== undefined)
      this.letter_spacing = props.letter_spacing;
    if (props.line_height !== undefined) this.line_height = props.line_height;

    // Text behavior
    if (props.text_wrap !== undefined) this.text_wrap = props.text_wrap;
    if (props.text_clip !== undefined) this.text_clip = props.text_clip;
    if (props.text_transform !== undefined)
      this.text_transform = props.text_transform;

    // Background properties
    if (props.background_color !== undefined)
      this.background_color = props.background_color;
    if (props.background_x_padding !== undefined)
      this.background_x_padding = props.background_x_padding;
    if (props.background_y_padding !== undefined)
      this.background_y_padding = props.background_y_padding;
    if (props.background_border_radius !== undefined)
      this.background_border_radius = props.background_border_radius;
    if (props.background_align_threshold !== undefined)
      this.background_align_threshold = props.background_align_threshold;

    // Transcript properties
    if (props.transcript_source !== undefined)
      this.transcript_source = props.transcript_source;
    if (props.transcript_effect !== undefined)
      this.transcript_effect = props.transcript_effect;
    if (props.transcript_split !== undefined)
      this.transcript_split = props.transcript_split;
    if (props.transcript_placement !== undefined)
      this.transcript_placement = props.transcript_placement;
    if (props.transcript_maximum_length !== undefined)
      this.transcript_maximum_length = props.transcript_maximum_length;
    if (props.transcript_color !== undefined)
      this.transcript_color = props.transcript_color;
  }

  protected override getAdditionalProperties(): Record<string, unknown> {
    const properties: Record<string, unknown> = {
      // Core text properties
      text: this.text,
      font_family: this.font_family,
      font_weight: this.font_weight,
      font_style: this.font_style,
      font_size: this.font_size,
      font_size_minimum: this.font_size_minimum,
      font_size_maximum: this.font_size_maximum,
      // Note: fill_color, x_alignment, y_alignment have different default values than in Element.toJSON()
      x_alignment: this.x_alignment,
      y_alignment: this.y_alignment,
      fill_color: this.fill_color,
      // Text alignment and spacing
      letter_spacing: this.letter_spacing,
      line_height: this.line_height,
      // Text behavior
      text_wrap: this.text_wrap,
      text_clip: this.text_clip,
      text_transform: this.text_transform,
      // Background properties
      background_color: this.background_color,
      background_x_padding: this.background_x_padding,
      background_y_padding: this.background_y_padding,
      background_border_radius: this.background_border_radius,
      background_align_threshold: this.background_align_threshold,
      // Transcript properties
      transcript_source: this.transcript_source,
      transcript_effect: this.transcript_effect,
      transcript_split: this.transcript_split,
      transcript_placement: this.transcript_placement,
      transcript_maximum_length: this.transcript_maximum_length,
      transcript_color: this.transcript_color,
    };

    return properties;
  }

  /**
   * Creates a deep clone of this text element
   * Override the base Element.clone() to ensure we create a TextElement, not a generic Element
   */
  override clone(): TextElement {
    const el = new TextElement({
      ...this.getState(),
    });

    return el;
  }

  /**
   * Creates a TextElement from JSON data
   */
  static fromJSON(json: unknown): TextElement {
    // Validate that json is an object
    if (!json || typeof json !== 'object' || Array.isArray(json)) {
      throw new Error('Invalid JSON: expected object for TextElement');
    }

    // Type narrow to Record<string, unknown> after validation
    const data = json as Record<string, unknown>;

    // Helper function to safely get time property values
    const getTimeValue = (value: unknown): string | number | null => {
      if (typeof value === 'string' || typeof value === 'number') {
        return value;
      }
      return null;
    };

    // Parse time-based properties that Element would usually parse
    const parsedTime = Element.parseTimeProperty(
      getTimeValue(data.time) ??
        getTimeValue(data.start) ??
        getTimeValue(data.globalTime) ??
        0,
    );
    const parsedDuration = Element.parseTimeProperty(
      getTimeValue(data.duration),
    );

    // Ensure parsedTime is a number, default to 0 if null
    const timeValue = parsedTime !== null ? Number(parsedTime) : 0;

    return new TextElement({
      // Base element properties
      id: typeof data.id === 'string' ? data.id : undefined,
      name: typeof data.name === 'string' ? data.name : null,
      track: typeof data.track === 'number' ? data.track : null,
      time: timeValue,
      duration: parsedDuration,
      type: 'text', // Force text type
      dynamic: typeof data.dynamic === 'boolean' ? data.dynamic : false,
      locked: typeof data.locked === 'boolean' ? data.locked : false,
      visible: typeof data.visible === 'boolean' ? data.visible : true,
      x: typeof data.x === 'string' ? data.x : '50%',
      y: typeof data.y === 'string' ? data.y : '50%',
      width: typeof data.width === 'string' ? data.width : null, // TextElement default
      height: typeof data.height === 'string' ? data.height : null, // TextElement default

      // Transform properties (now handled by Element)
      aspect_ratio:
        typeof data.aspect_ratio === 'number' ? data.aspect_ratio : null,
      x_padding: typeof data.x_padding === 'string' ? data.x_padding : '0 vw',
      y_padding: typeof data.y_padding === 'string' ? data.y_padding : '0 vh',
      z_index: typeof data.z_index === 'number' ? data.z_index : null,
      x_anchor: typeof data.x_anchor === 'string' ? data.x_anchor : '50%',
      y_anchor: typeof data.y_anchor === 'string' ? data.y_anchor : '50%',
      x_scale: typeof data.x_scale === 'string' ? data.x_scale : '100%',
      y_scale: typeof data.y_scale === 'string' ? data.y_scale : '100%',
      x_skew: typeof data.x_skew === 'string' ? data.x_skew : '0',
      y_skew: typeof data.y_skew === 'string' ? data.y_skew : '0',
      x_rotation: typeof data.x_rotation === 'string' ? data.x_rotation : '0',
      y_rotation: typeof data.y_rotation === 'string' ? data.y_rotation : '0',
      z_rotation: typeof data.z_rotation === 'string' ? data.z_rotation : '0',
      perspective:
        typeof data.perspective === 'number' ? data.perspective : null,
      backface_visible:
        typeof data.backface_visible === 'boolean'
          ? data.backface_visible
          : true,

      // Animation support (now handled by Element)
      animations: Array.isArray(data.animations) ? data.animations : [],
      color_overlay:
        Array.isArray(data.color_overlay) ||
        typeof data.color_overlay === 'string'
          ? data.color_overlay
          : null, // dip and crossfade animations

      // Core text properties
      text: typeof data.text === 'string' ? data.text : '',
      font_family:
        (typeof data.font_family === 'string' ? data.font_family : '') ||
        (typeof data.fontFamily === 'string' ? data.fontFamily : '') ||
        'Aileron',
      font_weight:
        typeof data.font_weight === 'number' ||
        typeof data.font_weight === 'string'
          ? data.font_weight
          : 400,
      font_style:
        typeof data.font_style === 'string' ? data.font_style : 'normal',
      font_size:
        typeof data.font_size === 'number' || typeof data.font_size === 'string'
          ? data.font_size
          : typeof data.fontSize === 'number' ||
              typeof data.fontSize === 'string'
            ? data.fontSize
            : null,
      font_size_minimum:
        typeof data.font_size_minimum === 'string'
          ? data.font_size_minimum
          : '1 vmin',
      font_size_maximum:
        typeof data.font_size_maximum === 'string'
          ? data.font_size_maximum
          : '100 vmin',
      // Note: fill_color, x_alignment, y_alignment will be set by Element constructor
      // Override with TextElement-specific defaults
      fill_color:
        (typeof data.fill_color === 'string' ? data.fill_color : '') ||
        (typeof data.color === 'string' ? data.color : '') ||
        '#000000',
      x_alignment:
        typeof data.x_alignment === 'string' ? data.x_alignment : '0%',
      y_alignment:
        typeof data.y_alignment === 'string' ? data.y_alignment : '0%',

      // Text stroke properties
      stroke_color:
        typeof data.stroke_color === 'string' ? data.stroke_color : null,
      stroke_width:
        typeof data.stroke_width === 'string' ? data.stroke_width : '0.1 vmin',

      // Text alignment and spacing
      letter_spacing:
        typeof data.letter_spacing === 'string' ? data.letter_spacing : '0%',
      line_height:
        typeof data.line_height === 'string' ? data.line_height : '115%',

      // Text behavior
      text_wrap: typeof data.text_wrap === 'boolean' ? data.text_wrap : true,
      text_clip: typeof data.text_clip === 'boolean' ? data.text_clip : false,
      text_transform:
        typeof data.text_transform === 'string' ? data.text_transform : 'none',

      // Background properties
      background_color:
        typeof data.background_color === 'string'
          ? data.background_color
          : null,
      background_x_padding:
        typeof data.background_x_padding === 'string'
          ? data.background_x_padding
          : '25%',
      background_y_padding:
        typeof data.background_y_padding === 'string'
          ? data.background_y_padding
          : '25%',
      background_border_radius:
        typeof data.background_border_radius === 'string'
          ? data.background_border_radius
          : '0%',
      background_align_threshold:
        typeof data.background_align_threshold === 'string'
          ? data.background_align_threshold
          : '20%',

      // Shadow properties
      shadow_color:
        typeof data.shadow_color === 'string' ? data.shadow_color : null,
      shadow_blur: typeof data.shadow_blur === 'number' ? data.shadow_blur : 0,
      shadow_x: typeof data.shadow_x === 'number' ? data.shadow_x : 0,
      shadow_y: typeof data.shadow_y === 'number' ? data.shadow_y : 0,

      // Transcript properties
      transcript_source: Array.isArray(data.transcript_source)
        ? (data.transcript_source as TranscriptSource)
        : null,
      transcript_effect:
        typeof data.transcript_effect === 'string'
          ? (data.transcript_effect as TranscriptEffect)
          : undefined,
      transcript_split:
        typeof data.transcript_split === 'string'
          ? (data.transcript_split as TranscriptSplit)
          : undefined,
      transcript_placement:
        typeof data.transcript_placement === 'string'
          ? (data.transcript_placement as TranscriptPlacement)
          : undefined,
      transcript_maximum_length:
        typeof data.transcript_maximum_length === 'number'
          ? data.transcript_maximum_length
          : undefined,
      transcript_color:
        typeof data.transcript_color === 'string'
          ? data.transcript_color
          : undefined,
    });
  }
}
